import { useRef, useMemo } from 'react';
import ProductCard from '@/components/base/ProductCard';
import { popularProducts } from '@/mocks/products';
import type { Product } from '@/mocks/products';
import { useDbPrices, mergeDbPrices } from '@/hooks/useDbPrices';
import { useAuth } from '@/hooks/useAuth';

// 카테고리별로 골고루 섞인 인기상품 순서
const popularOrder = [
  'kK01a',    // 1위 - 하하 맛김치 (김치·반찬)
  'hbHB07',   // 2위 - 바프 아몬드&믹스넛 12봉 (스낵·김)
  'drDR01',   // 3위 - 드레스룸 No.20 플라워샵 섬유향수 (섬유향수)
  'p1000',    // 4위 - 삼계탕 (탕류)
  'iIR01',    // 5위 - 황성주 검은콩두유 (음료·두유)
  'fF69',     // 6위 - 장흥무산 김부각 오리지널 (스낵·김)
  'wrWR01',   // 7위 - 월드런 슈플라이트3 (운동화)
  'atAT02',   // 8위 - 아토팜 판테놀 크림 (스킨케어·뷰티)
  'fF108',    // 9위 - 맛나 올리브김 (해조류)
  'hbHB02',   // 10위 - 바프 땅콩 6봉 (스낵·김)
  'drDR07',   // 11위 - 드레스퍼퓸 80ml (섬유향수)
  'p1001',    // 12위 - 소갈비탕 (탕류)
  'iIR07',    // 13위 - 황성주 과채습관 옐로우 (음료·두유)
  'wrWR05',   // 14위 - 월드런 카본Z4 (운동화)
  'atAT03',   // 15위 - 아토팜 MLE 크림 단지형 (스킨케어·뷰티)
  'fF70',     // 16위 - 장흥무산 김부각 와사비맛 (스낵·김)
  'drDR11',   // 17위 - 드레스퍼퓸 No.44 허니레몬티 (섬유향수)
  'p1003',    // 18위 - 곱창전골 (탕류)
  'nbNB01',   // 19위 - 아이스 넥밴드 (생활용품)
  'giGI01',   // 20위 - 건일 발매트 깔창 (건강용품)
  'drDR13',   // 20위 - 드레스퍼퓸 No.11 화이트솝 160ml (섬유향수)
];

const sortedPopular = [
  ...popularOrder
    .map(id => popularProducts.find(p => p.id === id))
    .filter((p): p is Product => !!p),
  ...popularProducts.filter(p => !popularOrder.includes(p.id)),
];

const popularIds = sortedPopular.map(p => p.id);

interface PopularSectionProps {
  onInquiry: (product: Product) => void;
}

export default function PopularSection({ onInquiry }: PopularSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { role } = useAuth();
  const { dbPrices } = useDbPrices(popularIds);

  const mergedProducts = useMemo(
    () => mergeDbPrices(sortedPopular, dbPrices, role),
    [dbPrices, role]
  );

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="popular"
      className="py-16 md:py-20 overflow-hidden"
      style={{ backgroundColor: '#EDE8DF', scrollMarginTop: 'calc(108px + var(--banner-height, 0px))' }}
    >
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: '#C4985A' }}>Popular</p>
            <h2 className="text-3xl md:text-4xl font-black leading-tight" style={{ color: '#2A2118' }}>热销商品</h2>
            <p className="text-base font-semibold mt-1" style={{ color: '#7A6E64' }}>인기 상품</p>
            <p className="text-sm mt-1" style={{ color: '#5C4F42' }}>
              韩中采购商订购最多的韩国优质商品。
              <span className="block text-xs font-medium mt-0.5" style={{ color: '#8C7D6E' }}>한국·중국 바이어들이 가장 많이 주문한 프리미엄 상품입니다.</span>
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
              style={{ border: '1px solid #D8CEBF', color: '#8C7D6E', backgroundColor: 'transparent' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#C4985A'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#C4985A'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#8C7D6E'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D8CEBF'; }}
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
              style={{ border: '1px solid #D8CEBF', color: '#8C7D6E', backgroundColor: 'transparent' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#C4985A'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#C4985A'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#8C7D6E'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D8CEBF'; }}
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal scroll */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 px-4 md:px-8 lg:px-12 scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {mergedProducts.map((product, i) => (
          <div
            key={product.id}
            className="shrink-0 w-[260px] sm:w-[280px] animate-fadeIn"
            style={{ scrollSnapAlign: 'start', animationDelay: `${i * 60}ms` }}
          >
            <ProductCard product={product} onInquiry={onInquiry} compact />
          </div>
        ))}
      </div>

      {/* Mobile arrows */}
      <div className="sm:hidden flex items-center justify-center gap-3 mt-4 px-4">
        <button
          onClick={() => scroll('left')}
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
          style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          onClick={() => scroll('right')}
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
          style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}
